import { SquadType } from '@scale/shared/types/expertManagement';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { SquadInfoCard } from 'react-scale/components/ExpertManagement/ExpertManagementPage/SquadInfoCard';
import { TeamLeadBreadCrumbs } from 'react-scale/components/ExpertManagement/ExpertManagementPage/TeamLeadBreadCrumbs';
import {
  benchmarkCards,
  communityCards,
  dolphinCards,
  qualityCards,
} from 'react-scale/components/ExpertManagement/ExpertManagementPage/cards';
import { useFetchSquadsForUser } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchSquadsForUser';
import {
  StatContextState,
  apiStatContextSelector,
  dateRangeSelector,
  projectIdSelector,
  useStatContextStore,
} from 'react-scale/components/ExpertManagement/stores/statContext.store';
import {
  useUserInfoStore,
  userInfoSelector,
} from 'react-scale/components/ExpertManagement/stores/userInfo.store';
import { shallow } from 'zustand/shallow';
import ChangeLogTable from './ChangeLogTable';
import { CommentSection } from './CommentSection';
import { ExpertTable } from './ExpertTable';
import { ManagerTable } from './ManagerTable';
import { StatContextSelector } from './StatContextSelector';
import { StatisticRow } from './StatisticRow';
import { UserInfoCard } from './UserInfoCard';
import { fetchStatsForManager } from './api';
import { ManagerStats } from './types';

const selectors = (state: StatContextState) => ({
  projectId: projectIdSelector(state),
  dateRange: dateRangeSelector(state),
  apiStatContext: apiStatContextSelector(state),
});

const ManagerPage: React.FC = () => {
  const maybeUserInfo = useUserInfoStore(userInfoSelector, shallow);
  const userInfo = maybeUserInfo!;
  const managerId = userInfo.id;
  const isPlatinum = userInfo.teamLeadPodType.isPlatinum;
  const { projectId, dateRange, apiStatContext } = useStatContextStore(selectors, shallow);
  const { data: squadsData } = useFetchSquadsForUser(managerId);
  const stats = useQuery(
    [`statsForManager`, managerId, projectId, dateRange],
    () => fetchStatsForManager(apiStatContext, managerId, squadsData?.squads?.[0]?._id ?? ''),
    {
      staleTime: 60 * 60 * 1000,
      enabled: !!squadsData?.squads?.[0]?._id,
    },
  );

  // eslint-disable-next-line complexity
  const cardRows = useMemo(() => {
    if (!stats.data?.stats) {
      return [];
    }
    const current: ManagerStats = stats.data.stats.current;
    const previous: ManagerStats = stats.data.stats.previous;
    const defaultColumns: any[] = [
      ...communityCards(current, previous),
      {
        title: 'Hours / Tasker',
        subtitle: 'Attempts + Reviews',
        value: Math.round((current.teamHours / current.teamSize) * 10) / 10,
        delta:
          Math.round(
            (current.teamHours / current.teamSize - previous.teamHours / previous.teamSize) * 10,
          ) / 10,
      },
    ];

    defaultColumns.push(
      ...qualityCards(
        {
          qmsScore: current.qmsStats.average,
          averageSbqPercentage: current.averageSbqPercentage,
          averageFractalGuardian: current.averageFractalGuardian,
          qualityControlScore: current.gqaStats.average,
          qualityControlPdr: current.gqaPdrStats.average,
          qualityControlCount: current.gqaStats.count,
          speedAuditScore: current.speedAuditStats.average,
          speedAuditPdr: current.speedAuditPdrStats.average,
          speedAuditCount: current.speedAuditStats.count,
          auditCoverage: current.auditCoverage,
          gqaAndSpeedAuditScore: current.gqaAndSpeedAuditStats.average,
        },
        {
          qmsScore: previous.qmsStats.average,
          averageSbqPercentage: previous.averageSbqPercentage,
          averageFractalGuardian: previous.averageFractalGuardian,
          qualityControlScore: previous.gqaStats.average,
          qualityControlPdr: previous.gqaPdrStats.average,
          qualityControlCount: previous.gqaStats.count,
          speedAuditScore: previous.speedAuditStats.average,
          speedAuditPdr: previous.speedAuditPdrStats.average,
          speedAuditCount: previous.speedAuditStats.count,
          gqaAndSpeedAuditScore: previous.gqaAndSpeedAuditStats.average,
        },
      ),
      ...benchmarkCards(
        { averageBmAccuracy: current.averageBmAccuracy },
        { averageBmAccuracy: previous.averageBmAccuracy },
      ),
    );

    if (current.dolphinQaStats?.count || previous.dolphinQaStats?.count) {
      defaultColumns.push(...dolphinCards(current.dolphinQaStats, previous.dolphinQaStats));
    }

    return defaultColumns;
  }, [stats.data]);

  const hasSubLeaders = stats.data?.squads?.length > 0;
  const hasExperts = stats.data?.experts?.length > 0;

  return (
    <div className="h-full w-full flex flex-col items-start gap-4">
      <div className="flex flex-col flex-4 gap-4 p-8 w-full">
        <TeamLeadBreadCrumbs />
        <StatContextSelector />

        <div className="flex justify-between gap-4">
          <div>
            {userInfo.squadType !== SquadType.Community ? (
              <UserInfoCard userInfo={userInfo} squadType={squadsData?.squads?.[0]?.type} />
            ) : (
              <SquadInfoCard userInfo={userInfo} squadType={squadsData?.squads?.[0]?.type} />
            )}
          </div>
          <div>
            <StatisticRow
              title="Squad Statistics"
              cards={cardRows}
              loadingCards={stats.isLoading ? 5 : 0}
            />
          </div>
        </div>
        {!stats.isLoading && hasSubLeaders && (
          <div style={{ maxWidth: 1680 }}>
            <div className="bg-neutral-0 p-4 border border-neutral-200 rounded-md">
              <ManagerTable
                title="Team Members: Leaders"
                data={stats.data?.squads}
                isLoading={stats.isLoading}
                reloadData={() => stats.refetch()}
              />
            </div>
          </div>
        )}
        {!(hasSubLeaders && !hasExperts) && (
          <div className="bg-neutral-0 p-4 border border-neutral-200 rounded-md">
            <ExpertTable
              userId={managerId}
              title="Team Members: Experts"
              data={stats.data?.experts}
              isLoading={stats.isLoading}
              reloadData={() => stats.refetch()}
              showPlatinum={isPlatinum}
            />
          </div>
        )}
        <div className="bg-neutral-0 p-4 border border-neutral-200 rounded-md w-fit">
          <ChangeLogTable managerId={managerId} title="Team Member Change Log" />
        </div>
      </div>
      <CommentSection targetId={userInfo.id} />
    </div>
  );
};

export default ManagerPage;
